import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import {
  displayDocumentName,
  formatMimeLabel,
} from '../../utils/documentDisplay';
import './DocumentReview.css';

interface DocumentReviewProps {
  applicationId: string;
  applicantName: string;
  onReviewComplete: (applicationId: string, status: 'approved' | 'rejected', comments: string) => void;
}

interface Document {
  id: string;
  type: 'nsfas_statement' | 'bank_statement' | 'student_card' | 'id_document';
  name: string;
  url: string;
  status: 'pending' | 'verified' | 'rejected';
  uploadedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  comments?: string;
  rejectionReason?: string;
  fileSize?: number;
  mimeType?: string;
}

const DocumentReview: React.FC<DocumentReviewProps> = ({ 
  applicationId, 
  applicantName, 
  onReviewComplete 
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [reviewComments, setReviewComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfPreviewError, setPdfPreviewError] = useState('');

  const isPdfDocument = (doc: Document) =>
    Boolean(doc.mimeType?.includes('pdf')) ||
    /\.pdf($|\?)/i.test(doc.url || '') ||
    doc.name.toLowerCase().endsWith('.pdf') ||
    (doc.url || '').includes('/raw/upload/');

  const isImageDocument = (doc: Document) =>
    !isPdfDocument(doc) &&
    (Boolean(doc.mimeType?.startsWith('image/')) || (doc.url || '').includes('/image/upload/'));

  const saveDocument = async (doc: Document, open: boolean) => {
    const blob = await adminAPI.fetchDocumentFile(doc.id);
    const type = blob.type || (isPdfDocument(doc) ? 'application/pdf' : 'application/octet-stream');
    const url = URL.createObjectURL(new Blob([blob], { type }));
    if (open) {
      window.open(url, '_blank');
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.download = doc.name || 'document';
    link.click();
  };

  useEffect(() => {
    if (!selectedDocument) {
      setPdfPreviewUrl(null);
      setPdfPreviewError('');
      return;
    }
    const created: { url: string } = { url: '' };
    let cancelled = false;
    setPdfPreviewError('');
    setPdfPreviewUrl(null);
    adminAPI.fetchDocumentFile(selectedDocument.id)
      .then((blob) => {
        if (cancelled) return;
        const type = blob.type || (isPdfDocument(selectedDocument) ? 'application/pdf' : blob.type);
        created.url = URL.createObjectURL(new Blob([blob], { type: type || 'application/octet-stream' }));
        setPdfPreviewUrl(created.url);
      })
      .catch((err: any) => {
        if (!cancelled) setPdfPreviewError(err?.message || 'Could not load this document');
      });
    return () => {
      cancelled = true;
      if (created.url) URL.revokeObjectURL(created.url);
    };
  }, [selectedDocument]);

  // Fetch documents for this application
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        console.log('🔍 Fetching documents for application:', applicationId);
        const response = await adminAPI.getApplicationDetails(applicationId);
        
        console.log('📦 API Response:', response);
        console.log('📦 Response data:', response.data);
        
        if (response.success && response.data) {
          // Map database documents to frontend Document interface
          const dbDocuments = (response.data as any).documents || [];
          console.log('📄 Raw documents from API:', dbDocuments);
          
          const mappedDocuments: Document[] = dbDocuments.map((doc: any) => ({
            id: doc.id,
            type: doc.document_type as Document['type'],
            name: displayDocumentName(
              doc.document_name || doc.file_name,
              doc.mime_type,
              doc.document_type
            ),
            url: doc.file_path || `/api/documents/${doc.id}`,
            status: doc.verification_status === 'verified' ? 'verified' : 
                   doc.verification_status === 'rejected' ? 'rejected' : 'pending',
            uploadedAt: doc.uploaded_at || doc.created_at,
            reviewedAt: doc.verified_at,
            reviewedBy: doc.verified_by,
            comments: doc.rejection_reason || undefined,
            rejectionReason: doc.rejection_reason || undefined,
            fileSize: doc.file_size,
            mimeType: doc.mime_type
          }));
          
          console.log('✅ Mapped documents:', mappedDocuments);
          setDocuments(mappedDocuments);
        } else {
          console.warn('⚠️ No documents found for application:', applicationId);
          console.warn('Response:', response);
          setDocuments([]);
        }
      } catch (error: any) {
        console.error('❌ Error fetching documents:', error);
        console.error('❌ Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          statusText: error.response?.statusText
        });
        // Fallback to empty array if fetch fails
        setDocuments([]);
      }
    };

    if (applicationId) {
      fetchDocuments();
    }
  }, [applicationId]);

  const getDocumentTypeName = (type: Document['type']) => {
    switch (type) {
      case 'nsfas_statement':
        return 'NSFAS Statement';
      case 'bank_statement':
        return 'Bank Statement';
      case 'student_card':
        return 'Student Card';
      case 'id_document':
        return 'ID Document';
      default:
        return type;
    }
  };

  const getStatusIcon = (status: Document['status']) => {
    switch (status) {
      case 'pending':
        return (
          <svg className="status-icon pending" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12,6 12,12 16,14"/>
          </svg>
        );
      case 'verified':
        return (
          <svg className="status-icon verified" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22,4 12,14.01 9,11.01"/>
          </svg>
        );
      case 'rejected':
        return (
          <svg className="status-icon rejected" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const getStatusColor = (status: Document['status']) => {
    switch (status) {
      case 'pending':
        return '#EA580C';
      case 'verified':
        return '#2ECC40';
      case 'rejected':
        return '#FF4136';
      default:
        return '#6b7280';
    }
  };

  const handleDocumentReview = async (documentId: string, status: 'verified' | 'rejected') => {
    setReviewing(true);
    
    try {
      // Call API to update document status
      const action = status === 'verified' ? 'verify' : 'reject';
      const notes = status === 'rejected' ? rejectionReason : reviewComments;
      
      const response = await adminAPI.processDocumentVerification(documentId, {
        action,
        notes: notes || undefined
      });

      if (response.success) {
        // Update local state with API response
        setDocuments(prev => 
          prev.map(doc => 
            doc.id === documentId 
              ? { 
                  ...doc, 
                  status, 
                  reviewedAt: response.data.verifiedAt || new Date().toISOString(),
                  reviewedBy: response.data.verifiedBy || 'admin-user',
                  comments: response.data.notes,
                  rejectionReason: status === 'rejected' ? response.data.notes : undefined
                }
              : doc
          )
        );
        
        setReviewComments('');
        setRejectionReason('');
        
        console.log('✅ Document verification updated:', response.data);
      } else {
        console.error('Failed to update document:', response);
        alert('Failed to update document status. Please try again.');
      }
      
    } catch (error: any) {
      console.error('Review error:', error);
      alert(`Error updating document: ${error.response?.data?.message || error.message}`);
    } finally {
      setReviewing(false);
    }
  };

  const handleApplicationReview = async (status: 'approved' | 'rejected') => {
    setReviewing(true);
    
    try {
      // Call the parent's review complete handler which will process the application
      // This will call the API and close the modal
      onReviewComplete(applicationId, status, reviewComments || '');
      
      // Note: Modal closing is handled by the parent component (ApplicationsQueue)
      // after the API call succeeds
    } catch (error) {
      console.error('Application review error:', error);
      setReviewing(false);
    }
    // Don't set reviewing to false here - let the parent handle it after API completes
  };

  const getOverallStatus = () => {
    const verifiedCount = documents.filter(doc => doc.status === 'verified').length;
    const rejectedCount = documents.filter(doc => doc.status === 'rejected').length;
    const pendingCount = documents.filter(doc => doc.status === 'pending').length;
    
    if (rejectedCount > 0) return 'rejected';
    if (pendingCount > 0) return 'pending';
    if (verifiedCount === documents.length) return 'verified';
    return 'pending';
  };

  const overallStatus = getOverallStatus();
  const canApprove = overallStatus === 'verified';

  return (
    <div className="document-review">
      <div className="review-header">
        <div className="applicant-info">
          <h2>
            <svg className="header-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            Document Review
          </h2>
          <p className="applicant-name">{applicantName}</p>
          <p className="application-id">Application ID: {applicationId}</p>
        </div>
        
        <div className="overall-status">
          <div 
            className="status-badge"
            style={{ backgroundColor: getStatusColor(overallStatus) }}
          >
            {overallStatus.toUpperCase()}
          </div>
        </div>
      </div>

      <div className="documents-list">
        <h3>Documents to Review</h3>
        {documents.length === 0 ? (
          <div className="no-documents">
            <svg className="empty-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            <p className="empty-message">No documents found for this application</p>
            <p className="empty-submessage">Documents may still be uploading or the application may not have any documents attached.</p>
          </div>
        ) : (
          <div className="documents-grid">
            {documents.map((document) => (
            <div key={document.id} className="document-item">
              <div className="document-header">
                <div className="document-info">
                  <h4>{getDocumentTypeName(document.type)}</h4>
                  <p className="document-name">{document.name}</p>
                  <p className="upload-date">
                    Uploaded: {new Date(document.uploadedAt).toLocaleDateString()}
                  </p>
                </div>
                
                <div className="document-status">
                  {getStatusIcon(document.status)}
                  <span 
                    className="status-text"
                    style={{ color: getStatusColor(document.status) }}
                  >
                    {document.status.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Document Preview Area */}
              <div className="document-preview">
                <div className="preview-thumbnail">
                  {isImageDocument(document) ? (
                    <div className="image-preview">
                      <img
                        src={document.url}
                        alt={document.name}
                        style={{ width: '100%', maxHeight: '120px', objectFit: 'contain', borderRadius: '4px' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="pdf-preview">
                      <svg className="pdf-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14,2 14,8 20,8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                      </svg>
                      <span className="file-type">PDF</span>
                    </div>
                  )}
                </div>
                
                <div className="preview-info">
                  <div className="file-details">
                    <span className="file-size">
                      {document.fileSize 
                        ? `${(document.fileSize / 1024 / 1024).toFixed(2)} MB` 
                        : 'N/A'}
                    </span>
                    <span className="file-date">{new Date(document.uploadedAt).toLocaleDateString()}</span>
                    <span className="file-format">{formatMimeLabel(document.mimeType, document.name)}</span>
                  </div>
                  <div className="preview-actions">
                    <button 
                      className="preview-button"
                      onClick={() => setSelectedDocument(document)}
                    >
                      <svg className="button-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                      Preview
                    </button>
                    <button 
                      className="download-button"
                      onClick={() => {
                        saveDocument(document, false).catch((err) => alert(err?.message || 'Download failed'));
                      }}
                    >
                      <svg className="button-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7,10 12,15 17,10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Download
                    </button>
                  </div>
                </div>
              </div>

              <div className="document-actions">
                
                {document.status === 'pending' && (
                  <div className="review-buttons">
                    <button 
                      className="approve-button"
                      onClick={() => handleDocumentReview(document.id, 'verified')}
                      disabled={reviewing}
                    >
                      <svg className="button-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22,4 12,14.01 9,11.01"/>
                      </svg>
                      Approve
                    </button>
                    <button 
                      className="reject-button"
                      onClick={() => handleDocumentReview(document.id, 'rejected')}
                      disabled={reviewing}
                    >
                      <svg className="button-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                      </svg>
                      Reject
                    </button>
                  </div>
                )}
              </div>

              {document.comments && (
                <div className="document-comments">
                  <strong>Comments:</strong> {document.comments}
                </div>
              )}

              {document.rejectionReason && (
                <div className="rejection-reason">
                  <strong>Rejection Reason:</strong> {document.rejectionReason}
                </div>
              )}
            </div>
          ))}
          </div>
        )}
      </div>

      <div className="review-notes">
        <h3>Review Notes</h3>
        <textarea
          value={reviewComments}
          onChange={(e) => setReviewComments(e.target.value)}
          placeholder="Add comments about the document review..."
          rows={4}
          className="notes-textarea"
        />
        
        {overallStatus === 'rejected' && (
          <div className="rejection-section">
            <label htmlFor="rejection-reason">Rejection Reason:</label>
            <textarea
              id="rejection-reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Explain why the application is being rejected..."
              rows={3}
              className="rejection-textarea"
            />
          </div>
        )}
      </div>

      <div className="review-actions">
        <button 
          className="approve-application-button"
          onClick={() => handleApplicationReview('approved')}
          disabled={!canApprove || reviewing}
        >
          <svg className="button-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22,4 12,14.01 9,11.01"/>
          </svg>
          Approve Application
        </button>
        
        <button 
          className="reject-application-button"
          onClick={() => handleApplicationReview('rejected')}
          disabled={reviewing}
        >
          <svg className="button-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          Reject Application
        </button>
      </div>

      {/* Document Viewer Modal */}
      {selectedDocument && (
        <div className="document-modal-overlay" onClick={() => setSelectedDocument(null)}>
          <div className="document-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{getDocumentTypeName(selectedDocument.type)}</h3>
              <button 
                className="modal-close"
                onClick={() => setSelectedDocument(null)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-content">
              <div className="document-preview">
                {isImageDocument(selectedDocument) ? (
                  <div className="image-preview-container">
                    <img 
                      src={pdfPreviewUrl || selectedDocument.url} 
                      alt={selectedDocument.name}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        const placeholder = (e.target as HTMLImageElement).nextElementSibling;
                        if (placeholder) (placeholder as HTMLElement).style.display = 'block';
                      }}
                      style={{ maxWidth: '100%', maxHeight: '500px', margin: '0 auto', display: 'block' }}
                    />
                    <div className="preview-placeholder" style={{ display: 'none' }}>
                      <div className="preview-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <polyline points="21,15 16,10 5,21"/>
                        </svg>
                      </div>
                      <p>{selectedDocument.name}</p>
                      <p className="preview-note">Image preview unavailable</p>
                    </div>
                  </div>
                ) : (
                  <div className="preview-placeholder">
                    <div className="preview-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14,2 14,8 20,8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                      </svg>
                    </div>
                    <p>{selectedDocument.name}</p>
                    <p className="preview-note">
                      {pdfPreviewError || (pdfPreviewUrl ? 'PDF loaded' : 'Loading PDF…')}
                    </p>
                    {pdfPreviewUrl && (
                      <iframe
                        title={selectedDocument.name}
                        src={pdfPreviewUrl}
                        style={{ width: '100%', height: '480px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}
                      />
                    )}
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1rem' }}>
                      <button 
                        className="preview-button"
                        onClick={() => saveDocument(selectedDocument, true).catch((err) => alert(err?.message || 'Could not open this PDF'))}
                        style={{ padding: '0.75rem 1.5rem' }}
                        disabled={!pdfPreviewUrl}
                      >
                        Open in New Tab
                      </button>
                      <button 
                        className="download-button"
                        onClick={() => saveDocument(selectedDocument, false).catch((err) => alert(err?.message || 'Download failed'))}
                        style={{ padding: '0.75rem 1.5rem' }}
                        disabled={!pdfPreviewUrl}
                      >
                        <svg className="button-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7,10 12,15 17,10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Download
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="document-details">
                <div className="detail-item">
                  <span className="detail-label">Type:</span>
                  <span className="detail-value">{getDocumentTypeName(selectedDocument.type)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status:</span>
                  <span 
                    className="detail-value"
                    style={{ color: getStatusColor(selectedDocument.status) }}
                  >
                    {selectedDocument.status.toUpperCase()}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Uploaded:</span>
                  <span className="detail-value">
                    {new Date(selectedDocument.uploadedAt).toLocaleString()}
                  </span>
                </div>
                {selectedDocument.reviewedAt && (
                  <div className="detail-item">
                    <span className="detail-label">Reviewed:</span>
                    <span className="detail-value">
                      {new Date(selectedDocument.reviewedAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentReview;
